import { useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';

import {
  CourseCardNameDocument,
  CourseCardNameQuery,
  CourseCardNameQueryVariables,
} from 'gqlhooks';

/**
 * Имена курсов по id — для чипов и выпадашек, где курс пришёл извне (из
 * ссылки), а в списке вариантов его нет. Читает `course(courseId)` (как
 * заголовок карточки курса, #132 — его читают все роли входа в ERP), по
 * одному запросу на курс, из кеша, если уже читали. Курс, которого нет или
 * не положено видеть, имени не получает — вызывающий решает, что показать.
 */
export const useCourseNames = (ids: string[]): Record<string, string> => {
  const client = useApolloClient();
  const [names, setNames] = useState<Record<string, string>>({});
  const key = useMemo(() => Array.from(new Set(ids)).sort().join(','), [ids]);

  useEffect(() => {
    if (!key) return undefined;
    let alive = true;
    key.split(',').forEach((id) => {
      client
        .query<CourseCardNameQuery, CourseCardNameQueryVariables>({
          query: CourseCardNameDocument,
          variables: { id },
          fetchPolicy: 'cache-first',
        })
        .then(({ data }) => {
          const name = data?.course?.name;
          if (!alive || !name) return;
          setNames((current) =>
            current[id] === name ? current : { ...current, [id]: name },
          );
        })
        .catch(() => undefined);
    });
    return () => {
      alive = false;
    };
  }, [client, key]);

  return names;
};
